// src/services/api/verificationAPI.js
import { config } from '../../config/env';

const FIREBASE_FUNCTIONS_BASE_URL = config.firebaseFunctionsBaseUrl
const SIMULATION_MODE = config.simulationMode;
const UNREGISTERED_CUSTOMER_BOOKING_ENABLED = config.unregisteredCustomerBookingEnabled;
const CUSTOMER_EXISTS = config.customerExists;

/**
 * Client-side API service for phone verification operations
 * Calls Firebase Functions for secure Twilio verification handling
 */
class VerificationAPI {

    static createBusinessHeaders() {
        return {
            'Content-Type': 'application/json'
        };
    }

    /**
     * Generate a unique device identifier for verification tracking
     * @returns {string} Device ID
     */
    static generateDeviceId() {
        // Create a stable device ID that persists across sessions
        const stored = localStorage.getItem('verification_device_id');
        if (stored) {
            return stored;
        }

        // Generate new device ID using timestamp + random + browser fingerprint
        const timestamp = Date.now().toString(36);
        const random = Math.random().toString(36).substring(2, 15);
        const fingerprint = this._generateBrowserFingerprint();
        const deviceId = `${timestamp}_${random}_${fingerprint}`;

        localStorage.setItem('verification_device_id', deviceId);
        return deviceId;
    }

    /**
     * Generate a simple browser fingerprint for device identification
     * @returns {string} Browser fingerprint
     * @private
     */
    static _generateBrowserFingerprint() {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        ctx.textBaseline = 'top';
        ctx.font = '14px Arial';
        ctx.fillText('Device fingerprint', 2, 2);

        const fingerprint = {
            userAgent: navigator.userAgent.slice(0, 50),
            language: navigator.language,
            platform: navigator.platform,
            screen: `${window.screen.width}x${window.screen.height}`,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            canvas: canvas.toDataURL().slice(-50),
        };

        return btoa(JSON.stringify(fingerprint)).slice(0, 16);
    }


    /**
     * Initiate phone number verification by sending SMS code
     * @param {string} phoneNumber - Phone number to verify (e.g., "+972501234567")
     * @param {string} deviceId - Optional device ID, will generate if not provided
     * @returns {Promise<Object>} Verification initiation result
     */
    static async initiateVerification(phoneNumber, businessSlug, deviceId = null) {
        try {
            console.log('API: Initiating verification', { phoneNumber, deviceId });

            // Validate phone number format
            if (!phoneNumber || typeof phoneNumber !== 'string') {
                throw new Error('Valid phone number is required');
            }

            if (!businessSlug || typeof businessSlug !== 'string') {
                throw new Error('Business slug is required');
            }

            // Generate device ID if not provided
            const finalDeviceId = deviceId || this.generateDeviceId();

            if (SIMULATION_MODE) {
                console.log('API: [SIMULATION] Successfully initiated verification', {
                    phoneNumber,
                    deviceId: finalDeviceId
                });

                // Simulate network delay
                await new Promise(resolve => setTimeout(resolve, 800));

                return {
                    success: true,
                    verificationStatus: 'pending',
                    attemptsRemaining: 3,
                    deviceId: finalDeviceId,
                    customerExists: true,
                    unregisteredCustomerBookingEnabled: UNREGISTERED_CUSTOMER_BOOKING_ENABLED
                };
            }

            return await this._retryRequest(async () => {
                const response = await fetch(`${FIREBASE_FUNCTIONS_BASE_URL}/verificationsHandlers`, {
                    method: 'POST',
                    headers: this.createBusinessHeaders(),
                    body: JSON.stringify({
                        operation: 'initiateVerification',
                        phoneNumber,
                        businessSlug,
                        deviceId: finalDeviceId,
                    })
                });

                if (!response.ok) {
                    await this._handleAPIError(response, 'initiateVerification');
                }

                const result = await response.json();

                if (!result.success) {
                    throw new Error(result.error || 'Failed to initiate verification');
                }

                console.log('API: Successfully initiated verification', {
                    phoneNumber,
                    deviceId: finalDeviceId,
                    status: result.data?.verificationStatus,
                });

                return {
                    ...result.data,
                    deviceId: finalDeviceId // Ensure device ID is returned
                };
            }, 1);

        } catch (error) {
            console.error('API Error in initiateVerification:', {
                phoneNumber,
                deviceId,
                error: error.message,
                stack: error.stack
            });

            throw new Error(`Failed to initiate verification: ${error.message}`);
        }
    }

    /**
     * Verify SMS code received by user
     * @param {string} phoneNumber - Phone number being verified
     * @param {string} code - 5-digit verification code from SMS
     * @param {string} deviceId - Device ID from initiation step
     * @returns {Promise<Object>} Verification result with JWT token
     */
    static async verifyCode(phoneNumber, code, businessSlug, deviceId) {
        try {
            console.log('API: Verifying code', {
                phoneNumber,
                deviceId,
                codeLength: code?.length
            });

            // Validate inputs
            if (!phoneNumber || typeof phoneNumber !== 'string') {
                throw new Error('Valid phone number is required');
            }

            if (!businessSlug || typeof businessSlug !== 'string') {
                throw new Error('Business slug is required');
            }

            if (!code || !/^\d{5}$/.test(code)) {
                throw new Error('Valid 5-digit verification code is required');
            }

            if (!deviceId || typeof deviceId !== 'string') {
                throw new Error('Device ID is required');
            }

            // SIMULATION MODE - Return dummy success response
            if (SIMULATION_MODE) {
                console.log('API: [SIMULATION] Successfully verified code', {
                    phoneNumber,
                    deviceId
                });

                // Simulate network delay
                await new Promise(resolve => setTimeout(resolve, 1000));

                return {
                    success: true,
                    verificationToken: `sim_token_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                    phoneNumber: phoneNumber
                };
            }

            return await this._retryRequest(async () => {
                const response = await fetch(`${FIREBASE_FUNCTIONS_BASE_URL}/verificationsHandlers`, {
                    method: 'POST',
                    headers: this.createBusinessHeaders(),
                    body: JSON.stringify({
                        operation: 'verifyCode',
                        phoneNumber,
                        code,
                        businessSlug,  // ADD this line
                        deviceId
                    })
                });

                if (!response.ok) {
                    await this._handleAPIError(response, 'verifyCode');
                }

                const result = await response.json();

                if (!result.success) {
                    throw new Error(result.error || 'Failed to verify code');
                }

                console.log('API: Successfully verified code', {
                    phoneNumber,
                    deviceId,
                    hasToken: !!result.data?.verificationToken
                });

                return result.data;
            });

        } catch (error) {
            console.error('API Error in verifyCode:', {
                phoneNumber,
                deviceId,
                codeLength: code?.length,
                error: error.message,
                stack: error.stack
            });

            throw new Error(`Failed to verify code: ${error.message}`);
        }
    }

    /**
     * Validate existing verification token
     * @param {string} token - JWT verification token
     * @param {string} deviceId - Device ID to validate against
     * @returns {Promise<Object>} Validation result
     */
    static async validateVerification(token, deviceId = null) {
        try {
            console.log('API: Validating verification token', {
                deviceId,
                hasToken: !!token
            });

            // Validate inputs
            if (!token || typeof token !== 'string') {
                throw new Error('Verification token is required');
            }

            // Use stored device ID if not provided
            const finalDeviceId = deviceId || this.generateDeviceId();

            // SIMULATION MODE - Return dummy success response
            if (SIMULATION_MODE) {
                console.log('API: [SIMULATION] Successfully initiated verification', {
                    token,
                    deviceId: finalDeviceId
                });

                // Simulate network delay
                await new Promise(resolve => setTimeout(resolve, 800));

                return {
                    success: true,
                    valid: true,
                    verificationStatus: 'pending',
                    attemptsRemaining: 3,
                    deviceId: finalDeviceId
                };
            }

            return await this._retryRequest(async () => {
                const response = await fetch(`${FIREBASE_FUNCTIONS_BASE_URL}/verificationsHandlers`, {
                    method: 'POST',
                    headers: this.createBusinessHeaders(),
                    body: JSON.stringify({
                        operation: 'validateVerification',
                        token,
                        deviceId: finalDeviceId
                    })
                });

                if (!response.ok) {
                    await this._handleAPIError(response, 'validateVerification');
                }

                const result = await response.json();

                if (!result.success) {
                    throw new Error(result.error || 'Failed to validate verification');
                }

                console.log('API: Successfully validated verification', {
                    deviceId: finalDeviceId,
                    valid: result.data?.valid,
                    phoneNumber: result.data?.phoneNumber
                });

                return result.data;
            });

        } catch (error) {
            console.error('API Error in validateVerification:', {
                deviceId,
                hasToken: !!token,
                error: error.message,
                stack: error.stack
            });

            throw new Error(`Failed to validate verification: ${error.message}`);
        }
    }

    /**
     * Get current device ID without generating a new one
     * @returns {string|null} Current device ID or null if none exists
     */
    static getCurrentDeviceId() {
        return localStorage.getItem('verification_device_id');
    }

    /**
     * Clear stored device ID (useful for testing or logout)
     */
    static clearDeviceId() {
        localStorage.removeItem('verification_device_id');
    }

    /**
     * Retry mechanism for network requests
     * @param {Function} requestFn - Function that makes the request
     * @param {number} maxRetries - Maximum number of retries
     * @returns {Promise} Request result
     * @private
     */
    static async _retryRequest(requestFn, maxRetries = 3) {
        let lastError;

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                return await requestFn();
            } catch (error) {
                lastError = error;

                // Don't retry on client errors (4xx) except 429 (rate limit)
                if (error.message.includes('400') || error.message.includes('401') ||
                    error.message.includes('403') || error.message.includes('404')) {
                    throw error;
                }

                // Don't retry on the last attempt
                if (attempt === maxRetries) {
                    break;
                }

                // Exponential backoff
                const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
                console.log(`API: Retrying request in ${delay}ms (attempt ${attempt}/${maxRetries})`);
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        }

        throw lastError;
    }

    /**
     * Handle API error responses
     * @param {Response} response - Fetch response object
     * @param {string} operation - Operation name for logging
     * @returns {Promise<void>}
     * @private
     */
    static async _handleAPIError(response, operation) {
        let errorMessage = `API request failed with status ${response.status}`;

        try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (parseError) {
            // If we can't parse the error response, use the status text
            errorMessage = response.statusText || errorMessage;
        }

        console.error(`API Error in ${operation}:`, {
            status: response.status,
            statusText: response.statusText,
            errorMessage
        });

        throw new Error(errorMessage);
    }
}

export default VerificationAPI;